import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Flag, Ban, Search, Paperclip, CheckCheck, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, errMsg, timeAgo, downloadBlob } from "@/lib/api";
import { useMe } from "@/lib/me";
import { EmptyState, Spinner } from "@/components/shared/common";
import { FileUpload, FileChips } from "@/components/shared/FileUpload";

export function Messages() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const { me, refresh } = useMe();
  const [convs, setConvs] = useState(null);
  const [q, setQ] = useState("");
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const end = useRef(null);

  useEffect(() => {
    const load = () => api.get("/conversations", { params: { q } }).then((r) => setConvs(r.data)).catch(() => setConvs([]));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [api, q]);

  useEffect(() => {
    if (!id) { setThread(null); return; }
    let stop = false;
    const load = () => api.get(`/conversations/${id}/messages`).then((r) => { if (!stop) setThread(r.data); }).catch(() => setThread({ error: true }));
    load(); refresh();
    const t = setInterval(load, 3000);
    return () => { stop = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, api]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [thread?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/conversations/${id}/messages`, { body, file_ids: files.map((f) => f.id) });
      setThread((t) => ({ ...t, messages: [...t.messages, data] })); setBody(""); setFiles([]);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const other = thread?.conversation?.participants?.find((u) => u !== me?.user_id);
  const block = async () => { try { const { data } = await api.post(`/users/${other}/block`); toast.success(data.blocked ? "User blocked" : "User unblocked"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const report = async () => { try { await api.post("/reports", { target_type: "conversation", target_id: id, reason: "Reported from inbox" }); toast.success("Reported to moderators"); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="container-x py-8">
      <div className="card-surface grid h-[calc(100vh-10rem)] min-h-[520px] overflow-hidden md:grid-cols-12">
        <div className={`flex-col border-r border-border md:col-span-4 ${id ? "hidden md:flex" : "flex"}`}>
          <div className="border-b border-border p-4"><div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" placeholder="Search messages" value={q} onChange={(e) => setQ(e.target.value)} data-testid="messages-search-input" /></div></div>
          <div className="flex-1 overflow-y-auto">
            {!convs ? <div className="p-6"><Spinner /></div> : convs.length === 0 ? <p className="p-6 text-sm text-muted-foreground">No conversations yet.</p> : convs.map((c) => (
              <button key={c.id} onClick={() => nav(`/messages/${c.id}`)} data-testid={`conversation-${c.id}`}
                className={`flex w-full flex-col gap-1 border-b border-border px-4 py-3 text-left transition-colors hover:bg-accent ${c.id === id ? "bg-accent" : ""}`}>
                <div className="flex items-center justify-between"><span className="truncate font-semibold">{c.other_name}</span><span className="text-xs text-muted-foreground">{timeAgo(c.last_message_at)}</span></div>
                {c.title && <span className="truncate text-xs text-primary">{c.title}</span>}
                <div className="flex items-center justify-between gap-2"><span className="truncate text-sm text-muted-foreground">{c.last_message}</span>{c.unread_count > 0 && <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground" data-testid="conversation-unread">{c.unread_count}</span>}</div>
              </button>))}
          </div>
        </div>
        <div className={`flex-col md:col-span-8 ${id ? "flex" : "hidden md:flex"}`}>
          {!id ? <div className="m-auto"><EmptyState title="Select a conversation" text="Messages with buyers and sellers appear here." /></div>
            : !thread ? <div className="m-auto"><Spinner /></div> : thread.error ? <div className="m-auto"><EmptyState title="Conversation not found" /></div> : (
            <>
              <div className="flex items-center justify-between border-b border-border px-5 py-3">
                <div><Link to="/messages" className="mr-2 text-sm text-primary md:hidden">←</Link><span className="font-semibold">{thread.conversation.names?.[other]}</span>
                  {thread.conversation.order_id && <Link to={`/orders/${thread.conversation.order_id}`} className="ml-2 text-xs text-primary">View order</Link>}</div>
                <div className="flex gap-1"><Button variant="ghost" size="sm" onClick={report} data-testid="report-conversation-button"><Flag className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={block} data-testid="block-user-button"><Ban className="h-4 w-4" />{thread.blocked ? " Unblock" : ""}</Button></div>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid="message-feed">
                {thread.messages.map((m) => {
                  const mine = m.sender_user_id === me?.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                        {m.body && <p className="whitespace-pre-line">{m.body}</p>}
                        {m.attachments.map((f) => <button key={f.id} className="mt-1 flex items-center gap-1 underline" onClick={() => downloadBlob(api, `/files/${f.id}/private`, f.original_filename)}><Paperclip className="h-3 w-3" />{f.original_filename}</button>)}
                        <p className={`mt-1 flex items-center gap-1 text-[10px] ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}{mine && m.read_by.length > 1 && <><CheckCheck className="h-3 w-3" />Read</>}</p>
                      </div>
                    </div>);
                })}
                <div ref={end} />
              </div>
              <form onSubmit={send} className="space-y-2 border-t border-border p-4">
                <FileChips files={files} onRemove={(fid) => setFiles(files.filter((f) => f.id !== fid))} />
                <div className="flex gap-2">
                  <FileUpload label="" onUploaded={(f) => setFiles((x) => [...x, f])} testId="message-attachment-input" />
                  <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a message…" data-testid="chat-message-input" />
                  <Button type="submit" disabled={!body.trim() && !files.length} data-testid="chat-send-message-button"><Send className="h-4 w-4" /></Button>
                </div>
              </form>
            </>)}
        </div>
      </div>
    </div>
  );
}

export function Notifications() {
  const api = useApi();
  const { refresh } = useMe();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/notifications").then((r) => { setItems(r.data); api.post("/notifications/read").then(refresh); }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  return (
    <div className="container-x max-w-3xl py-12">
      <h1 className="mb-8 text-3xl font-extrabold tracking-tight">Notifications</h1>
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState icon={Bell} title="You're all caught up" /> : (
        <div className="card-surface divide-y divide-border" data-testid="notifications-list">{items.map((n) => (
          <Link key={n.id} to={n.link || "#"} className={`flex items-start gap-3 p-4 transition-colors hover:bg-accent ${n.read ? "" : "bg-accent/50"}`} data-testid={`notification-${n.id}`}>
            <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-primary"}`} />
            <div className="flex-1"><p className="text-sm font-medium">{n.title}</p>{n.body && <p className="text-sm text-muted-foreground">{n.body}</p>}</div>
            <span className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
          </Link>))}</div>)}
    </div>
  );
}
